import React, { useState, useEffect, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Button,
  Paper,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  FormControl,
  Select,
  InputLabel,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@material-ui/core";
import {
  DeleteOutline,
  FileCopy,
  Cloud,
  PowerSettingsNew,
  Refresh,
  Settings,
  Subject,
  MoreVert,
  SystemUpdateAlt,
  InfoOutlined,
} from "@material-ui/icons";
import Chip from "@material-ui/core/Chip";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import AddLinkIcon from "@mui/icons-material/AddLink";

import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import DefaultPluginsPanel from "../../components/DefaultPluginsPanel";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../../components/ConfirmationModal";
import ChannelIcon, {
  useChannelPlugins,
  clearPluginsCache,
} from "../../components/ChannelIcon";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import PluginSettingsForm from "../../components/PluginSettingsForm";
import PluginAboutDialog from "../../components/PluginAboutDialog";
import {
  temNovidade,
  marcarVersaoVista,
  registrarVersoesConhecidas,
} from "../../utils/pluginNovidades";
import { i18n } from "../../translate/i18n";
import PluginBridge from "../../components/PluginBridge";
import PluginHtmlInline from "../../components/PluginBridge/PluginHtmlInline";
import { plugins as pluginsUI } from "@markedesk/plugin-sdk/ui";

const SLOTS = pluginsUI.slots;

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  pluginGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: 16,
    marginTop: 16,
  },
  pluginCard: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  pluginHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
    paddingRight: 32, // espaço pro botão de ações (kebab) absoluto no canto
  },
  pluginInfo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  capabilities: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 4,
  },
  capChip: {
    height: 20,
    fontSize: 11,
  },
  typeChip: {
    height: 24,
    fontSize: 11,
  },
  pluginActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 4,
    marginTop: "auto",
    paddingTop: 8,
    borderTop: "1px solid #eee",
  },
  baseUrl: {
    fontSize: 11,
    color: "#888",
    marginTop: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 200,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
  },
  filtroOrigem: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 4,
  },
  origemChip: {
    height: 20,
    fontSize: 10,
  },
  "@global": {
    "@keyframes spin": {
      "0%": { transform: "rotate(0deg)" },
      "100%": { transform: "rotate(360deg)" },
    },
  },
}));

const channelCapLabels = {
  sendText: "Texto",
  sendMedia: "Midia",
  sendReaction: "Reacoes",
  groups: "Grupos",
  editMessage: "Editar",
  deleteMessage: "Deletar",
};

const providesLabels = {
  channel: { label: "Canal", color: "#2196f3" },
  hooks: { label: "Hooks", color: "#ff9800" },
  automationSteps: { label: "Automacao", color: "#9c27b0" },
  jobs: { label: "Jobs", color: "#4caf50" },
  panels: { label: "Painel", color: "#e91e63" },
  routes: { label: "Rotas", color: "#607d8b" },
  actions: { label: "Acoes", color: "#00bcd4" },
};

/**
 * Deriva o motivo de bloqueio/aviso de compat a partir dos três gates que o
 * backend já calcula (POST /plugins/preview e GET /plugins retornam o mesmo
 * shape): sdkCompat (SDK do plugin atrás do que o backend exige), backendCompat
 * (backend atrás do que o plugin exige) e sdkRangeCompat (SDK fora da faixa
 * que o próprio plugin declarou). Cada gate só existe no objeto se o campo
 * correspondente foi declarado — retrocompat: plugin antigo sem nenhum desses
 * não bloqueia/avisa. Retorna null ou uma string com o motivo.
 *
 * `context`: "register" (modal de cadastro, ainda vai registrar) ou "card"
 * (plugin já cadastrado, listagem) — só muda o verbo final da mensagem.
 */
function getPluginCompatBlockReason(plugin, context = "register") {
  if (!plugin) return null;
  const cta = i18n.t(
    context === "register" ? "plugins.compat.ctaRegister" : "plugins.compat.ctaRestore"
  );
  if (plugin.sdkCompat?.sdkCompatible === false) {
    const versionSuffix = plugin.sdkCompat.sdkVersion
      ? i18n.t("plugins.compat.versionSuffix", { version: plugin.sdkCompat.sdkVersion })
      : "";
    const minSuffix = plugin.sdkCompat.backendMinSdkVersion
      ? i18n.t("plugins.compat.sdkOutdatedMinSuffix", { min: plugin.sdkCompat.backendMinSdkVersion })
      : "";
    return i18n.t("plugins.compat.sdkOutdated", { versionSuffix, minSuffix, cta });
  }
  if (plugin.backendCompat?.backendCompatible === false) {
    return i18n.t("plugins.compat.backendOutdated", {
      minBackendVersion: plugin.backendCompat.minBackendVersion,
      backendVersion: plugin.backendCompat.backendVersion,
      cta
    });
  }
  if (plugin.sdkRangeCompat?.inRange === false) {
    const { sdkVersion, minSdkVersion, maxSdkVersion } = plugin.sdkRangeCompat;
    const range = [
      minSdkVersion ? `>= ${minSdkVersion}` : null,
      maxSdkVersion ? `<= ${maxSdkVersion}` : null,
    ].filter(Boolean).join(` ${i18n.t("plugins.compat.rangeAnd")} `);
    return i18n.t("plugins.compat.sdkOutOfRange", { sdkVersion, range, cta });
  }
  return null;
}

const Plugins = () => {
  const { user } = useContext(AuthContext);
  // Só o dono da plataforma configura o que é obrigatório para todo mundo.
  const ehMaster = !!user?.masterAdmin;
  const [aba, setAba] = useState(0);
  const classes = useStyles();
  const theme = useTheme();
  const plugins = useChannelPlugins();

  // Primeira visita apenas anota o que existe hoje — sem selo, porque não há
  // nada de novo a contar. É o que faz o aviso de atualização valer para quem
  // nunca abriu a ficha do plugin, que é justamente quem mais precisa dele.
  useEffect(() => {
    registrarVersoesConhecidas(plugins, user?.id);
  }, [plugins, user?.id]);
  // Ao abrir a página, invalida o cache e refetcha o /plugins — o badge de
  // status (online/offline do transporte) reflete o estado atual do backend.
  useEffect(() => {
    clearPluginsCache();
  }, []);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [pluginToRemove, setPluginToRemove] = useState(null);
  // Plugin cuja ficha (quem mantém / suporte) está aberta.
  const [pluginSobre, setPluginSobre] = useState(null);
  // Filtro por ORIGEM. A lista misturava três coisas que se administram de
  // formas diferentes: o que vem com o produto, o que a plataforma impõe a
  // todas as empresas e o que esta empresa instalou. Só o último é de fato
  // gerenciável por quem está olhando.
  const [filtroOrigem, setFiltroOrigem] = useState("todos");
  // Menu de ações (kebab) por plugin: âncora + plugin atual.
  const [actionsMenu, setActionsMenu] = useState({ anchor: null, plugin: null });
  const openActionsMenu = (e, plugin) => setActionsMenu({ anchor: e.currentTarget, plugin });
  const closeActionsMenu = () => setActionsMenu({ anchor: null, plugin: null });
  const [remoteUrl, setRemoteUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [generatedApiKey, setGeneratedApiKey] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [settingsPlugin, setSettingsPlugin] = useState(null);
  const [settingsValues, setSettingsValues] = useState({});
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [reloadingPluginId, setReloadingPluginId] = useState(null);
  const [logsPlugin, setLogsPlugin] = useState(null);
  const [logsData, setLogsData] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsLevel, setLogsLevel] = useState("");
  const [logsLimit, setLogsLimit] = useState(200);
  const [logsSearch, setLogsSearch] = useState("");

  // Buscar metadata ao digitar URL (debounce 800ms) — via backend para evitar CORS
  useEffect(() => {
    setPreview(null);
    setPreviewError("");

    if (!remoteUrl || !remoteUrl.startsWith("http")) return;

    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const { data } = await api.post("/plugins/preview", {
          baseUrl: remoteUrl,
        });
        setPreview(data);
        setPreviewError("");
      } catch (err) {
        setPreview(null);
        const msg =
          err?.response?.data?.error || "Nao foi possivel conectar ao servidor";
        setPreviewError(msg);
      } finally {
        setPreviewLoading(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [remoteUrl]);

  const handleAddPlugin = async () => {
    if (!remoteUrl) {
      toast.error("URL do servidor e obrigatoria");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/plugins/remote", {
        baseUrl: remoteUrl,
      });
      toast.success(`Plugin "${data.metadata.displayName}" registrado!`);
      clearPluginsCache();

      // Backend gera a apiKey — mostra pra copiar.
      if (data.apiKey) {
        setGeneratedApiKey(data.apiKey);
      } else {
        handleCloseAddModal();
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setRemoteUrl("");
    setGeneratedApiKey("");
    setPreview(null);
    setPreviewError("");
  };

  const handleOpenSettings = async (plugin) => {
    setSettingsPlugin(plugin);
    setSettingsLoading(true);
    try {
      const { data } = await api.get(`/plugins/${plugin.id}/settings`);
      // Preencher com defaults dos fields que nao tem valor
      const defaults = {};
      (plugin.provides?.settings || []).forEach((f) => {
        if (f.defaultValue !== undefined) defaults[f.name] = f.defaultValue;
      });
      setSettingsValues({ ...defaults, ...data });
    } catch {
      setSettingsValues({});
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!settingsPlugin) return;
    setSettingsLoading(true);
    try {
      await api.put(`/plugins/${settingsPlugin.id}/settings`, settingsValues);
      toast.success("Configuracoes salvas");
      setSettingsPlugin(null);
    } catch (err) {
      toastError(err);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleReloadPlugin = async (plugin) => {
    const pluginId = plugin.id || plugin.channel;
    if (!pluginId) return;
    setReloadingPluginId(pluginId);
    try {
      const { data } = await api.post(`/plugins/${pluginId}/reload`);
      toast.success(
        `Plugin ${plugin.displayName || pluginId} recarregado` +
        (data?.version ? ` (v${data.version})` : "") +
        (data?.hooksCount != null ? ` · ${data.hooksCount} hooks` : "")
      );
      clearPluginsCache();
    } catch (err) {
      toastError(err);
    } finally {
      setReloadingPluginId(null);
    }
  };

  const handleReprovisionPlugin = async (plugin) => {
    const pluginId = plugin.id || plugin.channel;
    if (!pluginId) return;
    setReloadingPluginId(pluginId);
    try {
      const { data } = await api.post(`/plugins/${pluginId}/reprovision`);
      toast.success(
        `Plugin ${plugin.displayName || pluginId} reprovisionado` +
        (data?.restarted ? ` · ${data.restarted} conexao(oes) reiniciada(s)` : "")
      );
      clearPluginsCache();
    } catch (err) {
      toastError(err);
    } finally {
      setReloadingPluginId(null);
    }
  };

  const fetchLogs = async (plugin, level, limit) => {
    const pluginId = plugin.id || plugin.channel;
    if (!pluginId) return;
    setLogsLoading(true);
    try {
      const params = { limit: limit || logsLimit };
      if (level) params.level = level;
      const { data } = await api.get(`/plugins/${pluginId}/logs`, { params });
      setLogsData(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
      setLogsData([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleOpenLogs = (plugin) => {
    setLogsPlugin(plugin);
    setLogsLevel("");
    setLogsSearch("");
    setLogsLimit(200);
    fetchLogs(plugin, "", 200);
  };

  const handleRemovePlugin = async (pluginId) => {
    if (!pluginId) {
      toast.error("Plugin sem identificador");
      return;
    }
    try {
      await api.delete(`/plugins/${pluginId}`);
      toast.success("Plugin removido");
      clearPluginsCache();
      setPluginToRemove(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleCopyApiKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.info("API Key copiada!");
  };

  // Contexto dos slots da PÁGINA (cabeçalho e painel). Contrato em
  // `plugins.slotContext` / `PluginsPageSlotContext` no SDK — quem mexer aqui
  // atualiza lá, senão o plugin lê um campo que não existe mais.
  //
  // `instalados` sai daqui porque a página já fez a consulta: um painel de loja
  // marca o que está instalado sem refazer a chamada.
  //
  // `tema` vai junto porque o HTML de um plugin é injetado no DOM desta página
  // e divide a tela com ela: sem receber as cores, a única saída do plugin é
  // adivinhar o tema — e a única pista que o CSS tem sozinho
  // (`prefers-color-scheme`) é a do SISTEMA OPERACIONAL, que não tem relação
  // com o tema que o app escolheu. App escuro + Windows claro = painel branco
  // no meio da tela preta. Contrato: `TemaDoApp` no SDK.
  const contextoDaPagina = useMemo(
    () => ({
      total: plugins.length,
      instalados: plugins.map((p) => p.id || p.channel).filter(Boolean),
      ehMaster,
      // O plugin monta o HTML no servidor dele, longe do PluginRuntime do
      // navegador: sem isto, não teria como saber em que língua responder.
      idioma: i18n.language,
      tema: {
        modo: theme.palette.type,
        primaria: theme.palette.primary.main,
        primariaTexto: theme.palette.primary.contrastText,
        superficie: theme.palette.background.paper,
        fundo: theme.palette.background.default,
        texto: theme.palette.text.primary,
        textoSecundario: theme.palette.text.secondary,
        borda: theme.palette.divider,
        sucesso: theme.palette.success?.main || "#4caf50",
      },
    }),
    [plugins, ehMaster, theme]
  );

  // Origem só existe em backend novo: sem o campo, tudo cai em "company" e a
  // tela continua funcionando (só não separa) em vez de esvaziar a lista.
  const origemDoPlugin = (p) => p.origem || (p.isRemote ? "company" : "builtin");

  const contagemPorOrigem = useMemo(() => {
    const n = { todos: plugins.length, builtin: 0, required: 0, company: 0 };
    for (const p of plugins) n[origemDoPlugin(p)] = (n[origemDoPlugin(p)] || 0) + 1;
    return n;
  }, [plugins]);

  const pluginsVisiveis = useMemo(
    () =>
      filtroOrigem === "todos"
        ? plugins
        : plugins.filter((p) => origemDoPlugin(p) === filtroOrigem),
    [plugins, filtroOrigem]
  );

  // Abas declaradas por plugins (slot plugins-page-tabs). O app não conhece
  // nenhuma delas pelo nome: a aba existe porque um plugin a declarou, e o
  // conteúdo é servido pelo próprio plugin.
  const abasDePlugin = useMemo(() => {
    const encontradas = [];
    for (const p of plugins) {
      for (const a of p.provides?.actions || []) {
        if (a.slot === SLOTS.PAGE_TABS) encontradas.push({ ...a, plugin: p });
      }
    }
    return encontradas;
  }, [plugins]);

  // As abas nativas vêm primeiro; as de plugin seguem. "Padrões" só existe para
  // o master, então o deslocamento muda conforme quem está olhando — daí
  // calcular em vez de fixar o índice.
  const abasNativas = ehMaster ? 2 : 1;
  const abaDePluginSelecionada =
    aba >= abasNativas ? abasDePlugin[aba - abasNativas] : null;

  /**
   * Algum plugin tem ação para o rodapé DESTE card?
   *
   * O rodapé tem borda superior: renderizá-lo sempre desenharia uma linha solta
   * em todo card sem ação. Repete o filtro por canal do SlotRenderer — action de
   * plugin de canal só aparece no card daquele canal; plugin sem canal aparece
   * em todos.
   */
  const temAcaoDePluginNoCard = (plugin) =>
    plugins.some((p) => {
      const canalDoPlugin = p.provides?.channel?.channel;
      if (canalDoPlugin && plugin.channel && canalDoPlugin !== plugin.channel) return false;
      return (p.provides?.actions || []).some((a) => a.slot === SLOTS.CARD_ACTIONS);
    });

  /** Contexto dos slots de UM card. Contrato: `PluginCardSlotContext`. */
  const contextoDoCard = (plugin) => ({
    pluginId: plugin.id || plugin.channel,
    channel: plugin.channel,
    displayName: plugin.displayName,
    version: plugin.version,
    isRemote: !!plugin.isRemote,
    offline: !!plugin.offline,
    baseUrl: plugin.baseUrl,
    provides: plugin.provides || {},
    plugin,
  });

  return (
    <MainContainer>
      <MainHeader>
        <Title>Plugins ({plugins.length})</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="default"
            onClick={() => clearPluginsCache()}
            startIcon={<Refresh />}
          >
            Atualizar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setAddModalOpen(true)}
            startIcon={<AddLinkIcon />}
          >
            Adicionar Plugin Remoto
          </Button>
          <PluginBridge slot={SLOTS.PAGE_HEADER} context={contextoDaPagina} />
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {/* Faixa de largura total para painéis de plugin (ex: loja). Fica acima
          da lista e fora das abas de propósito: vale para qualquer usuário,
          não só para o master. */}
      <PluginBridge
        slot={SLOTS.PAGE_PANEL}
        as="text-buttons"
        context={contextoDaPagina}
      />

      {(ehMaster || abasDePlugin.length > 0) && (
        <Tabs
          value={aba}
          onChange={(_e, v) => setAba(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {/* Array, não fragment: em MUI v4 um <Tab> dentro de fragment não
              recebe o value e a aba não seleciona. Por isso as abas de plugin
              entram com spread, e não como lista aninhada. */}
          {[
            <Tab key="instalados" label={i18n.t("plugins.abas.instalados")} />,
            ...(ehMaster
              ? [<Tab key="padroes" label={i18n.t("plugins.abas.padroes")} />]
              : []),
            ...abasDePlugin.map((a) => (
              <Tab key={`${a.plugin.id || a.plugin.channel}:${a.id}`} label={a.label} />
            )),
          ]}
        </Tabs>
      )}

      {abaDePluginSelecionada ? (
        // Conteúdo servido pelo plugin: POST no endpoint dele, HTML injetado no
        // contexto do app (mesma sessão, sem iframe).
        <Paper className={classes.mainPaper} variant="outlined">
          <PluginHtmlInline
            block
            routePath={abaDePluginSelecionada.plugin.routePath}
            endpoint={
              abaDePluginSelecionada.endpoint || `/${abaDePluginSelecionada.id}`
            }
            html={abaDePluginSelecionada.html}
            context={contextoDaPagina}
          />
        </Paper>
      ) : ehMaster && aba === 1 ? (
        <DefaultPluginsPanel />
      ) : (
      <Paper className={classes.mainPaper} variant="outlined">
        {/* Filtro por origem: separa o que veio com o produto, o que a
            plataforma impõe e o que esta empresa instalou. Botões só aparecem
            quando há o que filtrar — com uma origem só, a barra seria ruído. */}
        {plugins.length > 0 && (
          <div className={classes.filtroOrigem}>
            {[
              { chave: "todos" },
              { chave: "required" },
              { chave: "company" },
              { chave: "builtin" },
            ]
              .filter((f) => f.chave === "todos" || contagemPorOrigem[f.chave] > 0)
              .map((f) => (
                <Chip
                  key={f.chave}
                  size="small"
                  clickable
                  label={`${i18n.t(`plugins.origem.${f.chave}`)} (${contagemPorOrigem[f.chave] || 0})`}
                  color={filtroOrigem === f.chave ? "primary" : "default"}
                  variant={filtroOrigem === f.chave ? "default" : "outlined"}
                  onClick={() => setFiltroOrigem(f.chave)}
                />
              ))}
          </div>
        )}
        {plugins.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Nenhum plugin instalado
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Plugins locais (Telegram, Discord) sao carregados automaticamente
              ao iniciar o servidor.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Para adicionar um plugin remoto, clique em "Adicionar Plugin
              Remoto".
            </Typography>
          </div>
        ) : (
          pluginsVisiveis.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="body2" color="textSecondary">
              {i18n.t("plugins.origem.vazio")}
            </Typography>
          </div>
        ) : (
          <div className={classes.pluginGrid}>
            {pluginsVisiveis.map((plugin) => (
              <Card key={plugin.channel} variant="outlined" style={{ position: "relative" }}>
                {/* Sem condição: "Sobre e suporte" existe para todo plugin,
                    então todo card tem ao menos uma ação. Antes o menu só
                    aparecia em plugin remoto/com logs — um plugin local ficaria
                    com a ficha inalcançável. */}
                <Tooltip title="Ações">
                  <IconButton
                    size="small"
                    onClick={(e) => openActionsMenu(e, plugin)}
                    style={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </Tooltip>
                <CardContent className={classes.pluginCard}>
                  <div className={classes.pluginHeader}>
                    <div className={classes.pluginInfo}>
                      <ChannelIcon
                        channel={plugin.channel}
                        style={{ fontSize: 32 }}
                      />
                      <div>
                        <Typography
                          variant="subtitle1"
                          style={{ fontWeight: 600, lineHeight: 1.2, display: "flex", alignItems: "center", gap: 6 }}
                        >
                          {plugin.displayName}
                          {plugin.isRemote && (
                            <span
                              title={plugin.offline ? "Backend sem conexão com o plugin" : "Conectado ao plugin"}
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 10,
                                color: plugin.offline ? "#b71c1c" : "#1b5e20",
                                background: plugin.offline ? "#ffebee" : "#e8f5e9",
                              }}
                            >
                              <span style={{
                                width: 7, height: 7, borderRadius: "50%",
                                background: plugin.offline ? "#e53935" : "#43a047",
                              }} />
                              {plugin.offline ? "offline" : "online"}
                            </span>
                          )}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          {plugin.id || plugin.channel}
                          {plugin.version && ` v${plugin.version}`}
                          {temNovidade(plugin, user?.id) && (
                            <Tooltip title={i18n.t("plugins.novidades.tooltip")}>
                              <Chip
                                size="small"
                                color="primary"
                                label={i18n.t("plugins.novidades.selo")}
                                style={{ height: 16, fontSize: 10 }}
                              />
                            </Tooltip>
                          )}
                          {(() => {
                            const reason = getPluginCompatBlockReason(plugin, "card");
                            return reason && (
                              <Tooltip title={reason}>
                                <SystemUpdateAlt style={{ fontSize: 13, color: "#f57c00" }} />
                              </Tooltip>
                            );
                          })()}
                        </Typography>
                        {plugin.description && (
                          <Typography variant="body2" color="textSecondary" style={{ fontSize: 11 }}>
                            {plugin.description}
                          </Typography>
                        )}
                        {/* Quem responde por este plugin, na própria listagem:
                            é a informação que o administrador procura quando
                            algo para de funcionar, e ela não deveria exigir
                            abrir um menu para aparecer. */}
                        {plugin.store?.maintainer?.name && (
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            style={{ display: "block", fontSize: 11 }}
                          >
                            {i18n.t("plugins.origem.porMantenedor", {
                              nome: plugin.store.maintainer.name,
                            })}
                            {plugin.store.partner?.name &&
                              i18n.t("plugins.origem.usaParceiro", {
                                nome: plugin.store.partner.name,
                              })}
                          </Typography>
                        )}
                        {plugin.baseUrl && (
                          <Tooltip title={plugin.baseUrl}>
                            <Typography className={classes.baseUrl}>
                              {plugin.baseUrl}
                            </Typography>
                          </Tooltip>
                        )}
                        {plugin.isRemote && plugin.offline && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            disabled={reloadingPluginId === (plugin.id || plugin.channel)}
                            onClick={() => handleReloadPlugin(plugin)}
                            style={{ marginTop: 6, textTransform: "none", fontSize: 11, padding: "1px 10px" }}
                          >
                            {reloadingPluginId === (plugin.id || plugin.channel)
                              ? "Reconectando…"
                              : "Reconectar plugin"}
                          </Button>
                        )}
                      </div>
                    </div>
                    <Chip
                      size="small"
                      icon={
                        plugin.isRemote ? (
                          <Cloud style={{ fontSize: 14 }} />
                        ) : (
                          <PowerSettingsNew style={{ fontSize: 14 }} />
                        )
                      }
                      label={plugin.isRemote ? "Remoto" : "Local"}
                      variant="outlined"
                      className={classes.typeChip}
                      color={plugin.isRemote ? "primary" : "default"}
                    />
                    {/* Obrigatório: a plataforma provisiona este plugin em toda
                        empresa. Sem a marca, removê-lo parece funcionar e ele
                        reaparece sozinho na próxima varredura — o card precisa
                        dizer isso antes de alguém tentar. */}
                    {origemDoPlugin(plugin) === "required" && (
                      <Tooltip title={i18n.t("plugins.origem.obrigatorioTooltip")}>
                        <Chip
                          size="small"
                          label={i18n.t("plugins.origem.obrigatorio")}
                          variant="outlined"
                          className={classes.typeChip}
                          style={{ color: "#f57c00", borderColor: "#f57c00" }}
                        />
                      </Tooltip>
                    )}
                    {plugin.offline && (
                      <Chip
                        size="small"
                        label="Offline"
                        variant="outlined"
                        className={classes.typeChip}
                        style={{ color: "#f44336", borderColor: "#f44336" }}
                      />
                    )}
                  </div>

                  {/* Capacidades do plugin (provides) + etiquetas de plugin */}
                  <div className={classes.capabilities}>
                    <PluginBridge
                      slot={SLOTS.CARD_BADGE}
                      context={contextoDoCard(plugin)}
                    />
                    {plugin.provides && Object.entries(providesLabels).map(
                      ([key, { label, color }]) => {
                        const val = plugin.provides[key];
                        const hasIt = val === true || (Array.isArray(val) && val.length > 0) || (typeof val === "object" && !Array.isArray(val) && val);
                        return hasIt ? (
                          <Chip
                            key={key}
                            size="small"
                            label={label}
                            className={classes.capChip}
                            style={{ backgroundColor: color, color: "#fff" }}
                          />
                        ) : null;
                      }
                    )}
                  </div>

                  {/* Capabilities de canal (se tem canal) */}
                  {plugin.provides?.channel && (
                    <div className={classes.capabilities} style={{ marginTop: 4 }}>
                      {Object.entries(channelCapLabels).map(
                        ([key, label]) =>
                          plugin.capabilities?.[key] && (
                            <Chip
                              key={key}
                              size="small"
                              label={label}
                              className={classes.capChip}
                            />
                          )
                      )}
                    </div>
                  )}

                  {/* Ação primária no rodapé; demais ações no menu "⋮" do topo. */}
                  {(plugin.provides?.settings?.length > 0 ||
                    temAcaoDePluginNoCard(plugin)) && (
                    <div className={classes.pluginActions}>
                      {plugin.provides?.settings?.length > 0 && (
                        <Button
                          size="small"
                          color="primary"
                          startIcon={<Settings style={{ fontSize: 16 }} />}
                          onClick={() => handleOpenSettings(plugin)}
                          style={{ textTransform: "none", fontSize: 12 }}
                        >
                          Configurar
                        </Button>
                      )}
                      <PluginBridge
                        slot={SLOTS.CARD_ACTIONS}
                        as="text-buttons"
                        context={contextoDoCard(plugin)}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ))}
      </Paper>
      )}

      {/* Menu de ações do plugin (kebab) */}
      <Menu
        anchorEl={actionsMenu.anchor}
        open={Boolean(actionsMenu.anchor)}
        onClose={closeActionsMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {/* Vale para qualquer plugin: mesmo quem não declarou nada precisa
            dizer isso a quem administra a empresa. */}
        <MenuItem
          onClick={() => {
            const alvo = actionsMenu.plugin;
            setPluginSobre(alvo);
            // Leu, some o selo: a ficha é onde as novidades estão.
            marcarVersaoVista(user?.id, alvo?.id || alvo?.channel, alvo?.version);
            closeActionsMenu();
          }}
        >
          <ListItemIcon style={{ minWidth: 32 }}><InfoOutlined fontSize="small" /></ListItemIcon>
          <ListItemText primary={i18n.t("plugins.sobre.acao")} />
        </MenuItem>
        {actionsMenu.plugin?.apiKey && (
          <MenuItem
            onClick={() => {
              handleCopyApiKey(actionsMenu.plugin.apiKey);
              closeActionsMenu();
            }}
          >
            <ListItemIcon style={{ minWidth: 32 }}><FileCopy fontSize="small" /></ListItemIcon>
            <ListItemText primary="Copiar API Key" />
          </MenuItem>
        )}
        {actionsMenu.plugin?.supportsLogs && (
          <MenuItem
            onClick={() => {
              handleOpenLogs(actionsMenu.plugin);
              closeActionsMenu();
            }}
          >
            <ListItemIcon style={{ minWidth: 32 }}><Subject fontSize="small" /></ListItemIcon>
            <ListItemText primary="Logs" />
          </MenuItem>
        )}
        {actionsMenu.plugin?.isRemote && (
          <MenuItem
            onClick={() => {
              handleReloadPlugin(actionsMenu.plugin);
              closeActionsMenu();
            }}
          >
            <ListItemIcon style={{ minWidth: 32 }}><Refresh fontSize="small" /></ListItemIcon>
            <ListItemText primary="Recarregar metadata" secondary="sem reiniciar" />
          </MenuItem>
        )}
        {actionsMenu.plugin?.isRemote && (
          <MenuItem
            onClick={() => {
              handleReprovisionPlugin(actionsMenu.plugin);
              closeActionsMenu();
            }}
          >
            <ListItemIcon style={{ minWidth: 32 }}><PowerSettingsNew fontSize="small" /></ListItemIcon>
            <ListItemText primary="Reprovisionar" secondary="resolve 503 e reinicia conexões" />
          </MenuItem>
        )}
        {actionsMenu.plugin?.isRemote && (
          <MenuItem
            onClick={() => {
              setPluginToRemove(actionsMenu.plugin);
              closeActionsMenu();
            }}
          >
            <ListItemIcon style={{ minWidth: 32 }}><DeleteOutline fontSize="small" color="error" /></ListItemIcon>
            <ListItemText primary="Remover" primaryTypographyProps={{ color: "error" }} />
          </MenuItem>
        )}
      </Menu>

      {/* Modal de adicionar plugin remoto */}
      {(() => {
        const compatBlockReason = getPluginCompatBlockReason(preview);
        return (
      <Dialog
        open={addModalOpen}
        onClose={handleCloseAddModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Adicionar Plugin Remoto</DialogTitle>
        <DialogContent>
          {generatedApiKey ? (
            <>
              <Typography
                variant="body1"
                gutterBottom
                style={{ marginBottom: 16 }}
              >
                Plugin registrado! Configure esta API Key no servidor remoto
                (variavel <code>API_KEY</code>):
              </Typography>
              <TextField
                fullWidth
                value={generatedApiKey}
                InputProps={{ readOnly: true }}
                variant="outlined"
                onClick={() => handleCopyApiKey(generatedApiKey)}
                helperText="Clique para copiar"
              />
            </>
          ) : (
            <>
              <TextField
                autoFocus
                margin="dense"
                label="URL do Servidor"
                placeholder="http://localhost:3033"
                fullWidth
                value={remoteUrl}
                onChange={(e) => setRemoteUrl(e.target.value)}
                helperText={
                  previewError || "URL base do servidor que roda o plugin (ex: http://servidor:3033)"
                }
                error={!!previewError}
                InputProps={{
                  endAdornment: previewLoading ? (
                    <CircularProgress size={20} />
                  ) : null,
                }}
              />

              {preview && (
                <Card
                  variant="outlined"
                  style={{
                    marginTop: 12,
                    marginBottom: 8,
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <CardContent style={{ padding: "12px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                      <ChannelIcon
                        channel={preview.id || preview.channel}
                        style={{ fontSize: 28 }}
                      />
                      <div>
                        <Typography variant="subtitle1" style={{ fontWeight: 600, lineHeight: 1.2 }}>
                          {preview.displayName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {preview.id || preview.channel}
                          {preview.version && ` v${preview.version}`}
                        </Typography>
                        {preview.description && (
                          <Typography variant="body2" color="textSecondary" style={{ fontSize: 12, marginTop: 2 }}>
                            {preview.description}
                          </Typography>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {/* Provides (formato novo) */}
                      {preview.provides && Object.entries(providesLabels).map(
                        ([key, { label, color }]) => {
                          const val = preview.provides[key];
                          const hasIt = val === true || (Array.isArray(val) && val.length > 0) || (typeof val === "object" && !Array.isArray(val) && val);
                          return hasIt ? (
                            <Chip key={key} size="small" label={label} className={classes.capChip} style={{ backgroundColor: color, color: "#fff" }} />
                          ) : null;
                        }
                      )}
                      {/* Fallback formato legado */}
                      {!preview.provides && preview.capabilities && Object.entries(channelCapLabels).map(
                        ([key, label]) =>
                          preview.capabilities?.[key] && (
                            <Chip key={key} size="small" label={label} className={classes.capChip} />
                          )
                      )}
                    </div>
                    {compatBlockReason && (
                      <div
                        style={{
                          display: "flex", alignItems: "flex-start", gap: 8,
                          marginTop: 10, padding: "8px 10px", borderRadius: 6,
                          backgroundColor: "#fff3e0", border: "1px solid #ffb74d",
                        }}
                      >
                        <SystemUpdateAlt style={{ fontSize: 18, color: "#e65100", flexShrink: 0, marginTop: 1 }} />
                        <Typography variant="body2" style={{ fontSize: 12, color: "#e65100" }}>
                          {compatBlockReason}
                        </Typography>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddModal}>
            {generatedApiKey ? "Fechar" : "Cancelar"}
          </Button>
          {!generatedApiKey && (
            <Tooltip title={compatBlockReason || ""}>
              <span>
                <Button
                  onClick={handleAddPlugin}
                  color="primary"
                  variant="contained"
                  disabled={loading || !remoteUrl || !!compatBlockReason}
                >
                  {loading ? <CircularProgress size={24} /> : "Registrar"}
                </Button>
              </span>
            </Tooltip>
          )}
        </DialogActions>
      </Dialog>
        );
      })()}

      {/* Ficha do plugin: o que faz, quem mantém, para onde pedir suporte.
          Os dados vêm declarados pelo próprio plugin (`store` no metadata). */}
      <PluginAboutDialog
        open={!!pluginSobre}
        plugin={pluginSobre}
        onClose={() => setPluginSobre(null)}
      />

      {/* Confirmacao de remocao */}
      <ConfirmationModal
        title="Remover Plugin"
        open={!!pluginToRemove}
        onClose={() => setPluginToRemove(null)}
        onConfirm={() => handleRemovePlugin(pluginToRemove?.id || pluginToRemove?.channel)}
      >
        Deseja remover o plugin{" "}
        <strong>{pluginToRemove?.displayName}</strong>? As conexoes que usam
        este plugin serao desconectadas.
      </ConfirmationModal>

      {/* Modal de configuracao do plugin */}
      {(() => {
        const layout = settingsPlugin?.provides?.settingsLayout || "md";
        const isFullscreen = layout === "fullscreen";
        const dialogProps = isFullscreen
          ? { fullScreen: true }
          : { maxWidth: layout, fullWidth: true };
        return (
      <Dialog
        open={!!settingsPlugin}
        onClose={() => setSettingsPlugin(null)}
        {...dialogProps}
      >
        <DialogTitle>
          Configurar {settingsPlugin?.displayName}
        </DialogTitle>
        <DialogContent>
          {settingsLoading ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <CircularProgress />
            </div>
          ) : (
            <PluginSettingsForm
              fields={settingsPlugin?.provides?.settings || []}
              tabs={settingsPlugin?.provides?.settingsTabs || []}
              values={settingsValues}
              onChange={setSettingsValues}
              pluginId={settingsPlugin?.id || settingsPlugin?.channel}
              routePath={settingsPlugin?.routePath}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsPlugin(null)}>Cancelar</Button>
          <Button
            onClick={handleSaveSettings}
            color="primary"
            variant="contained"
            disabled={settingsLoading}
          >
            {settingsLoading ? <CircularProgress size={24} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
        );
      })()}

      <Dialog
        open={!!logsPlugin}
        onClose={() => setLogsPlugin(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Logs · {logsPlugin?.displayName || logsPlugin?.id}
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
            <TextField
              variant="outlined"
              size="small"
              label="Buscar"
              placeholder="Filtrar texto..."
              value={logsSearch}
              onChange={(e) => setLogsSearch(e.target.value)}
              style={{ minWidth: 200, flex: 1 }}
            />
            <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
              <InputLabel>Nível</InputLabel>
              <Select
                label="Nível"
                value={logsLevel}
                onChange={(e) => {
                  setLogsLevel(e.target.value);
                  if (logsPlugin) fetchLogs(logsPlugin, e.target.value, logsLimit);
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="debug">Debug+</MenuItem>
                <MenuItem value="info">Info+</MenuItem>
                <MenuItem value="warn">Warn+</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" style={{ minWidth: 110 }}>
              <InputLabel>Limite</InputLabel>
              <Select
                label="Limite"
                value={logsLimit}
                onChange={(e) => {
                  setLogsLimit(e.target.value);
                  if (logsPlugin) fetchLogs(logsPlugin, logsLevel, e.target.value);
                }}
              >
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
                <MenuItem value={500}>500</MenuItem>
              </Select>
            </FormControl>
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={() => logsPlugin && fetchLogs(logsPlugin, logsLevel, logsLimit)}
              disabled={logsLoading}
            >
              Atualizar
            </Button>
          </div>

          <div
            style={{
              fontFamily: "monospace",
              fontSize: 12,
              background: "#1e1e1e",
              color: "#d4d4d4",
              borderRadius: 4,
              padding: 12,
              maxHeight: 420,
              overflow: "auto",
              whiteSpace: "pre-wrap",
            }}
          >
            {(() => {
              const term = logsSearch.trim().toLowerCase();
              const filtered = term
                ? logsData.filter((e) =>
                    (e.message || "").toLowerCase().includes(term)
                  )
                : logsData;
              if (logsLoading) {
                return (
                  <div style={{ textAlign: "center", padding: 20 }}>
                    <CircularProgress size={24} />
                  </div>
                );
              }
              if (filtered.length === 0) {
                return (
                  <div style={{ color: "#888" }}>
                    {term ? "Nenhum log corresponde ao filtro." : "Sem logs disponíveis."}
                  </div>
                );
              }
              return filtered.map((entry, i) => {
                const color =
                  entry.level === "error" ? "#f48771"
                    : entry.level === "warn" ? "#dcdcaa"
                    : entry.level === "debug" ? "#888"
                    : "#9cdcfe";
                const ts = entry.timestamp
                  ? new Date(entry.timestamp).toLocaleTimeString()
                  : "";
                return (
                  <div key={i}>
                    <span style={{ color: "#666" }}>{ts}</span>{" "}
                    <span style={{ color, fontWeight: "bold" }}>
                      [{(entry.level || "info").toUpperCase()}]
                    </span>{" "}
                    {entry.connectionId != null && (
                      <span style={{ color: "#608b4e" }}>(conn {entry.connectionId}) </span>
                    )}
                    {entry.message}
                  </div>
                );
              });
            })()}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsPlugin(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Plugins;
